import React, { useCallback, useMemo } from 'react';
import { useEmployeeEngagementDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/store/employeeEngagement.store';
import {
  useChartColors,
  ChartColorPalette,
} from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { DataTable } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/DataTable';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/EmployeeEngagementDashboard.styled';
import { DataTableColumn } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.types';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';

function getAcceptanceRateColor(rate: number, palette: ChartColorPalette): string {
  if (rate >= 75) return palette.indicator.positive;
  if (rate >= 60) return palette.threshold.attention;
  return palette.indicator.negative;
}

export const CalloutAcceptanceRateTable: React.FC = () => {
  const data = useEmployeeEngagementDashboardStore((s) => s.calloutAcceptance);
  const currentPage = useEmployeeEngagementDashboardStore((s) => s.calloutAcceptancePage);
  const totalPages = useEmployeeEngagementDashboardStore((s) => s.calloutAcceptanceTotalPages);
  const isLoading = useEmployeeEngagementDashboardStore((s) => s.calloutAcceptanceLoading);
  const hasError = useEmployeeEngagementDashboardStore((s) => s.calloutAcceptanceError);
  const fetchCalloutAcceptance = useEmployeeEngagementDashboardStore(
    (s) => s.fetchCalloutAcceptance,
  );
  const palette = useChartColors();

  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);

  const handlePageChange = useCallback(
    (page: number) => {
      fetchCalloutAcceptance(page, dateRange, locationIds, positions, employmentTypes);
    },
    [fetchCalloutAcceptance, dateRange, locationIds, positions, employmentTypes],
  );

  const renderAcceptanceRate = useMemo(
    () =>
      function AcceptanceRateCell(value: string | number): React.ReactNode {
        const rate = Number(value);
        if (value === '' || Number.isNaN(rate)) return '—';
        return (
          <span style={{ color: getAcceptanceRateColor(rate, palette), fontWeight: 600 }}>
            {`${formatDashboardNumber(rate)}%`}
          </span>
        );
      },
    [palette],
  );

  const columns: DataTableColumn[] = useMemo(
    () => [
      { key: 'teamMemberName', label: 'Team member' },
      { key: 'position', label: 'Position' },
      { key: 'type', label: 'Type' },
      { key: 'calloutsSent', label: 'Call outs sent', align: 'right', noWrap: true },
      { key: 'calloutsAccepted', label: 'Call outs Accepted', align: 'right', noWrap: true },
      {
        key: 'acceptanceRate',
        label: 'Acceptance rate',
        align: 'right',
        noWrap: true,
        renderCell: renderAcceptanceRate,
      },
      { key: 'averageResponseSpeed', label: 'Avg. response speed', align: 'right', noWrap: true },
    ],
    [renderAcceptanceRate],
  );

  const rows = useMemo(
    () =>
      data?.map((d) => ({
        teamMemberName: d.teamMemberName,
        position: d.position,
        type: d.type,
        calloutsSent: d.calloutsSent,
        calloutsAccepted: d.calloutsAccepted,
        acceptanceRate: d.acceptanceRate ?? NaN,
        averageResponseSpeed:
          d.averageResponseSpeedMinutes == null
            ? '—'
            : `${formatDashboardNumber(d.averageResponseSpeedMinutes)} min`,
      })) ?? [],
    [data],
  );

  if (hasError) {
    return <EmptyStateContainer>Failed to load call-out acceptance data</EmptyStateContainer>;
  }

  if (!data && !isLoading) {
    return <EmptyStateContainer>No data available</EmptyStateContainer>;
  }

  return (
    <DataTable
      columns={columns}
      rows={rows}
      ariaLabel="Call-out acceptance rate and response speed table"
      serverPagination={{
        currentPage,
        totalPages,
        onPageChange: handlePageChange,
        isLoading,
      }}
    />
  );
};
